import React, { useState, useEffect } from 'react';
import { Tag, Sparkles, Ticket, ListChecks, ShieldQuestion, Trash2, UserCheck } from 'lucide-react';
import {
  WizardWorkspace,
  VerticalStack,
  HorizontalStack,
  SectionEyebrow,
  TechnicalValue,
  CommerceObjectSelector,
  CurrencyInput,
  type CommerceSelectOptionViewModel,
} from '@workspace/silk-ui';
import {
  type DiscountFormState,
  type EligibilityScopeDraft,
  DISCOUNT_TYPE_OPTIONS,
  CONDITION_TYPE_OPTIONS,
  createEmptyEligibilityScope,
  PROMOTION_END_DEFAULT_TIME,
  PROMOTION_START_DEFAULT_TIME,
  scheduleDatePart,
  scheduleTimePart,
  selectDiscountType,
  updateScheduleDate,
  updateScheduleTime,
} from '@/features/commerce-admin/promotionEditorContract';

export type { DiscountFormState };

type EligibilitySelectorConfig = {
  options: CommerceSelectOptionViewModel[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage: string;
};

interface PromotionWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (form: DiscountFormState) => Promise<void>;
  initialForm: DiscountFormState;
  isEditing: boolean;
  eligibilityValueConfigs: Record<string, EligibilitySelectorConfig>;
  selectorLoading: boolean;
}

export function PromotionWizard({
  isOpen,
  onClose,
  onSave,
  initialForm,
  isEditing,
  eligibilityValueConfigs,
  selectorLoading,
}: PromotionWizardProps) {
  const [activeTabId, setActiveTabId] = useState('identity');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<DiscountFormState>(initialForm);

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm);
      setActiveTabId('identity');
      setSaveError('');
    }
  }, [isOpen, initialForm]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      await onSave(form);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save promotion.';
      setSaveError(message);
      if (message.toLowerCase().includes('autopay')) {
        setActiveTabId('conditions');
        window.setTimeout(() => {
          const fieldId = message.toLowerCase().includes('description')
            ? 'autopay-description'
            : message.toLowerCase().includes('option')
              ? 'autopay-true-label'
              : message.toLowerCase().includes('disclosure')
                ? 'autopay-disclosure'
                : 'autopay-label';
          document.getElementById(fieldId)?.focus();
        }, 0);
      } else if (message.toLowerCase().includes('eligibility')) {
        setActiveTabId('eligibility');
      } else if (message.toLowerCase().includes('condition')) {
        setActiveTabId('conditions');
      } else if (message.toLowerCase().includes('code')) {
        setActiveTabId('codes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const setField = <K extends keyof DiscountFormState>(field: K, value: DiscountFormState[K]) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const setAutoPayField = <K extends keyof DiscountFormState['autoPayConfiguration']>(
    field: K,
    value: DiscountFormState['autoPayConfiguration'][K],
  ) => {
    setForm(current => ({
      ...current,
      autoPayConfiguration: { ...current.autoPayConfiguration, [field]: value },
    }));
  };

  const handleDiscountTypeChange = (value: string) => {
    const newDiscountType = value as DiscountFormState['discountType'];
    setForm(current => selectDiscountType(current, newDiscountType));
  };

  const addEligibilityScope = () => {
    setForm(current => ({ ...current, eligibilityScopes: [...current.eligibilityScopes, createEmptyEligibilityScope()] }));
  };

  const removeEligibilityScope = (index: number) => {
    setForm(current => ({ ...current, eligibilityScopes: current.eligibilityScopes.filter((_, i) => i !== index) }));
  };

  const updateEligibilityScope = <K extends keyof EligibilityScopeDraft>(
    index: number,
    field: K,
    value: EligibilityScopeDraft[K],
  ) => {
    setForm(current => {
      const scopes = [...current.eligibilityScopes];
      scopes[index] = { ...scopes[index], [field]: value };
      return { ...current, eligibilityScopes: scopes };
    });
  };

  const tabs = [
    { id: 'identity', label: 'Identity & Schedule', icon: <Tag className="h-4 w-4" /> },
    { id: 'benefit', label: 'Benefit & Stack', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'conditions', label: 'Conditions', icon: <ShieldQuestion className="h-4 w-4" /> },
    { id: 'eligibility', label: 'Eligibility', icon: <UserCheck className="h-4 w-4" /> },
    { id: 'codes', label: 'Codes & Display', icon: <Ticket className="h-4 w-4" /> },
    { id: 'review', label: 'Review', icon: <ListChecks className="h-4 w-4" /> },
  ];

  const renderContent = () => {
    switch (activeTabId) {
      case 'identity':
        return (
          <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Promotion Identity & Schedule</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Define the core details and timing of your promotion.</p>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Promotion Key</label>
                <input
                  value={form.promotionKey}
                  onChange={(e) => setField('promotionKey', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                  disabled={isEditing}
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Promotion Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2} className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label htmlFor="discount-type" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Discount Type</label>
                 <select
                  id="discount-type"
                  value={form.discountType}
                  onChange={(e) => handleDiscountTypeChange(e.target.value)}
                  className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  {DISCOUNT_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Redemption Method</label>
                <select
                  value={form.redemptionMethod}
                  onChange={(e) => setField('redemptionMethod', e.target.value as DiscountFormState['redemptionMethod'])}
                  disabled={form.discountType !== 'code_required'}
                  className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  <option value="automatic">Automatic</option>
                  <option value="customer_code">Customer Code</option>
                  <option value="hidden_code">Hidden Code</option>
                </select>
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setField('status', e.target.value)}
                  className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </VerticalStack>
              <VerticalStack gap={2} />
              <VerticalStack gap={2}>
                <label htmlFor="promotion-start-date" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Start date</label>
                <input
                  id="promotion-start-date"
                  type="date"
                  value={scheduleDatePart(form.startsAt)}
                  onChange={(e) => setField('startsAt', updateScheduleDate(form.startsAt, e.target.value, PROMOTION_START_DEFAULT_TIME))}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label htmlFor="promotion-start-time" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Start time</label>
                <input
                  id="promotion-start-time"
                  type="time"
                  step="60"
                  value={scheduleTimePart(form.startsAt)}
                  onChange={(e) => setField('startsAt', updateScheduleTime(form.startsAt, e.target.value, PROMOTION_START_DEFAULT_TIME))}
                  disabled={!scheduleDatePart(form.startsAt)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label htmlFor="promotion-end-date" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Expiration date</label>
                <input
                  id="promotion-end-date"
                  type="date"
                  value={scheduleDatePart(form.endsAt)}
                  onChange={(e) => setField('endsAt', updateScheduleDate(form.endsAt, e.target.value, PROMOTION_END_DEFAULT_TIME))}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label htmlFor="promotion-end-time" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Expiration time</label>
                <input
                  id="promotion-end-time"
                  type="time"
                  step="60"
                  value={scheduleTimePart(form.endsAt)}
                  onChange={(e) => setField('endsAt', updateScheduleTime(form.endsAt, e.target.value, PROMOTION_END_DEFAULT_TIME))}
                  disabled={!scheduleDatePart(form.endsAt)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                />
              </VerticalStack>
            </div>
          </VerticalStack>
        );

      case 'benefit':
        return (
           <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Stacking & Benefit</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Configure the economic benefit customers receive and how it stacks.</p>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Stack Policy</label>
                <select
                  value={form.stackPolicy}
                  onChange={(e) => setField('stackPolicy', e.target.value)}
                  className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  <option value="exclusive">Exclusive</option>
                  <option value="combinable">Combinable</option>
                  <option value="priority">Priority</option>
                </select>
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Stack Group</label>
                <input
                  value={form.stackGroup}
                  onChange={(e) => setField('stackGroup', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Priority</label>
                <input
                  value={form.priority}
                  onChange={(e) => setField('priority', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Benefit Type</label>
                <select
                  value={form.benefitType}
                  onChange={(e) => { setField('benefitType', e.target.value); if (e.target.value === 'free_periods' && !['order', 'line_item'].includes(form.appliesTo)) setField('appliesTo', 'order'); }}
                  className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  <option value="percent_off">Percent Off</option>
                  <option value="amount_off">Amount Off</option>
                  <option value="fixed_price">Fixed Price</option>
                  <option value="fee_waiver">Fee Waiver</option>
                  <option value="free_periods">Free Billing Periods</option>
                </select>
              </VerticalStack>
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Applies To</label>
                <select
                  value={form.appliesTo}
                  onChange={(e) => setField('appliesTo', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                >
                  <option value="order">Order</option>
                  <option value="line_item">Line Item</option>
                  {form.benefitType !== 'free_periods' && <option value="installation">Installation</option>}
                  {form.benefitType !== 'free_periods' && <option value="shipping">Shipping</option>}
                </select>
              </VerticalStack>
               {form.benefitType !== 'free_periods' && (<VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Currency</label>
                <input
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>)}
              {['amount_off', 'fixed_price'].includes(form.benefitType) && (
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Amount</label>
                  <CurrencyInput
                    value={form.amount}
                    onChange={(e) => setField('amount', e.target.value)}
                    placeholder="e.g., 10.00"
                  />
                </VerticalStack>
              )}
              {form.benefitType === 'percent_off' && (
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Percentage</label>
                  <input
                    value={form.percentage}
                    onChange={(e) => setField('percentage', e.target.value)}
                    placeholder="e.g., 15.5 for 15.5%"
                    className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                  />
                </VerticalStack>
              )}
              {form.benefitType !== 'free_periods' && (<VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Maximum Discount</label>
                <CurrencyInput
                  value={form.maxDiscount}
                  onChange={(e) => setField('maxDiscount', e.target.value)}
                  placeholder="Optional ceiling"
                />
              </VerticalStack>)}
              {form.benefitType === 'free_periods' && (
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Application Policy</label>
                  <select value={form.applicationPolicy} onChange={(e) => setField('applicationPolicy', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background">
                    <option value="overlap">Overlap from the first cycle</option>
                    <option value="extend_free_periods">Extend existing free periods</option>
                  </select>
                  <p className="text-sm text-muted-foreground">Applies to recurring charges only. Extension adds free cycles after other free-period benefits on the same charge. Other discounts keep their original duration.</p>
                </VerticalStack>
              )}
              <VerticalStack gap={2}>
                <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{form.benefitType === 'free_periods' ? 'Free Billing Cycles' : 'Discount Length'}</label>
                <input
                  value={form.discountLength}
                  onChange={(e) => setField('discountLength', e.target.value)}
                  placeholder={form.benefitType === 'free_periods' ? 'e.g., 2' : 'Recurring billing periods; blank means indefinite'}
                  className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                />
              </VerticalStack>
            </div>
          </VerticalStack>
        );

      case 'conditions':
        return (
          <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Conditions</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Define the conditions that must be met for this promotion to apply.</p>

            {form.discountType === 'standard' && <p>Standard discounts do not have special conditions.</p>}
            {form.discountType === 'code_required' && <p>Code-required discounts are conditioned on a valid code entry.</p>}

            {form.discountType === 'conditional' && (
              <VerticalStack gap={6}>
                 <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Condition Type</label>
                  <input
                    value="AutoPay enabled"
                    readOnly
                    className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold text-muted-foreground outline-none"
                  />
                </VerticalStack>
                <SectionEyebrow>AutoPay Configuration</SectionEyebrow>
                 <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <VerticalStack gap={2}>
                      <label htmlFor="autopay-label" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Choice Label</label>
                      <input id="autopay-label" value={form.autoPayConfiguration.label} onChange={e => setAutoPayField('label', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2}>
                      <label htmlFor="autopay-required" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Customer Response</label>
                      <select id="autopay-required" value={form.autoPayConfiguration.required ? 'required' : 'optional'} onChange={e => setAutoPayField('required', e.target.value === 'required')} className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background">
                        <option value="required">Required before checkout</option>
                        <option value="optional">Optional</option>
                      </select>
                    </VerticalStack>
                    <VerticalStack gap={2} className="sm:col-span-2">
                      <label htmlFor="autopay-description" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Customer Explanation</label>
                      <textarea id="autopay-description" value={form.autoPayConfiguration.description} onChange={e => setAutoPayField('description', e.target.value)} rows={2} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                     <VerticalStack gap={2}>
                      <label htmlFor="autopay-true-label" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">AutoPay Option Label</label>
                      <input id="autopay-true-label" value={form.autoPayConfiguration.trueLabel} onChange={e => setAutoPayField('trueLabel', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2}>
                      <label htmlFor="autopay-false-label" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Standard Billing Option Label</label>
                      <input id="autopay-false-label" value={form.autoPayConfiguration.falseLabel} onChange={e => setAutoPayField('falseLabel', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                     <VerticalStack gap={2} className="sm:col-span-2">
                      <label htmlFor="autopay-disclosure" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Checkout Disclosure</label>
                      <textarea id="autopay-disclosure" value={form.autoPayConfiguration.disclosure} onChange={e => setAutoPayField('disclosure', e.target.value)} rows={2} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2} className="sm:col-span-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Deferred Enrollment Message</label>
                      <textarea value={form.autoPayConfiguration.deferredEnrollmentCopy} onChange={e => setAutoPayField('deferredEnrollmentCopy', e.target.value)} rows={2} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                </div>
              </VerticalStack>
            )}

            {form.discountType === 'context' && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <VerticalStack gap={2}>
                  <label htmlFor="context-condition-type" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Condition Type</label>
                  <select
                    id="context-condition-type"
                    value={form.conditionType}
                    onChange={(e) => setField('conditionType', e.target.value)}
                    className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                  >
                    {CONDITION_TYPE_OPTIONS.filter(o => o.discountType === 'context').map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </VerticalStack>
                <VerticalStack gap={2}>
                  <label htmlFor="context-condition-value" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Condition Value</label>
                  <input
                    id="context-condition-value"
                    value={form.conditionValue}
                    onChange={(e) => setField('conditionValue', e.target.value)}
                    className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                  />
                </VerticalStack>
              </div>
            )}
          </VerticalStack>
        );

      case 'eligibility':
        const eligibilityScopeTypes = Object.keys(eligibilityValueConfigs);
        return (
           <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Eligibility</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Define which customers, products, or markets are eligible.</p>
            <VerticalStack gap={4}>
              {form.eligibilityScopes.map((scope, index) => (
                <HorizontalStack key={index} gap={2} className="items-center rounded-md border border-border/60 bg-muted/20 p-4">
                  <VerticalStack gap={2} className="flex-grow">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <select
                        aria-label={`Eligibility scope ${index + 1} type`}
                        value={scope.scopeType}
                        onChange={(e) => {
                          updateEligibilityScope(index, 'scopeType', e.target.value);
                          updateEligibilityScope(index, 'scopeValue', '');
                        }}
                        className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                      >
                        {eligibilityScopeTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                       <CommerceObjectSelector
                        options={eligibilityValueConfigs[scope.scopeType]?.options ?? []}
                        value={scope.scopeValue}
                        onChange={(value) => updateEligibilityScope(index, 'scopeValue', value)}
                        placeholder={eligibilityValueConfigs[scope.scopeType]?.placeholder ?? 'Select value'}
                        searchPlaceholder={eligibilityValueConfigs[scope.scopeType]?.searchPlaceholder ?? 'Search scope values...'}
                        emptyMessage={eligibilityValueConfigs[scope.scopeType]?.emptyMessage ?? 'No options'}
                        loading={selectorLoading}
                      />
                       <select
                        value={scope.exclude ? 'exclude' : 'include'}
                        onChange={(e) => updateEligibilityScope(index, 'exclude', e.target.value === 'exclude')}
                        className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background"
                      >
                        <option value="include">Include</option>
                        <option value="exclude">Exclude</option>
                      </select>
                    </div>
                  </VerticalStack>
                   <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground" aria-label={`Remove eligibility scope ${index + 1}`} onClick={() => removeEligibilityScope(index)}>
                      <Trash2 className="h-4 w-4" />
                   </button>
                </HorizontalStack>
              ))}
            </VerticalStack>
            <button type="button" className="inline-flex h-9 w-fit items-center rounded-md border border-border/60 px-3 text-sm font-bold text-foreground transition-colors hover:bg-muted/40" onClick={addEligibilityScope}>Add Eligibility Scope</button>
          </VerticalStack>
        );

      case 'codes':
        return (
          <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Codes & Display</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Configure redemption codes and storefront display metadata.</p>

            {form.redemptionMethod !== 'automatic' && !isEditing && (
              <VerticalStack gap={6}>
                  <SectionEyebrow>Redemption Code</SectionEyebrow>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <VerticalStack gap={2}>
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Redemption Code</label>
                      <input value={form.code} onChange={(e) => setField('code', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2}>
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Code Status</label>
                      <select value={form.codeStatus} onChange={(e) => setField('codeStatus', e.target.value)} className="w-full appearance-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background">
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="archived">Archived</option>
                      </select>
                    </VerticalStack>
                     <VerticalStack gap={2}>
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Code Use Limit</label>
                      <input value={form.codeUseLimit} onChange={(e) => setField('codeUseLimit', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2}>
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Code Starts At</label>
                      <input type="datetime-local" value={form.codeStartsAt} onChange={(e) => setField('codeStartsAt', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                    <VerticalStack gap={2}>
                      <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Code Ends At</label>
                      <input type="datetime-local" value={form.codeEndsAt} onChange={(e) => setField('codeEndsAt', e.target.value)} className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm font-bold outline-none transition-all focus:border-primary/40 focus:bg-background" />
                    </VerticalStack>
                  </div>
              </VerticalStack>
            )}

            <VerticalStack gap={2}>
              <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Display JSON</label>
              <textarea
                value={form.displayJSON}
                onChange={(e) => setField('displayJSON', e.target.value)}
                rows={8}
                className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 font-mono text-xs outline-none transition-all focus:border-primary/40 focus:bg-background"
              />
            </VerticalStack>
            <VerticalStack gap={2}>
              <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Metadata JSON</label>
              <textarea
                value={form.metadataJSON}
                onChange={(e) => setField('metadataJSON', e.target.value)}
                rows={8}
                className="w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2 font-mono text-xs outline-none transition-all focus:border-primary/40 focus:bg-background"
              />
            </VerticalStack>
          </VerticalStack>
        );

      case 'review':
        return (
          <VerticalStack gap={6} className="max-w-3xl">
            <SectionEyebrow>Review & Create</SectionEyebrow>
            <p className="text-sm text-muted-foreground">Verify the promotion details before saving.</p>

            <div className="rounded-lg border border-border/40 bg-muted/5 p-6">
              <VerticalStack gap={6}>
                <HorizontalStack className="justify-between border-b border-border/40 pb-4">
                  <span className="text-sm font-bold text-muted-foreground">Promotion Key</span>
                  <TechnicalValue>{form.promotionKey || 'Not set'}</TechnicalValue>
                </HorizontalStack>
                <HorizontalStack className="justify-between border-b border-border/40 pb-4">
                  <span className="text-sm font-bold text-muted-foreground">Name</span>
                  <span className="text-sm font-medium">{form.name || 'None'}</span>
                </HorizontalStack>
                <HorizontalStack className="justify-between border-b border-border/40 pb-4">
                  <span className="text-sm font-bold text-muted-foreground">Status & Type</span>
                  <span className="text-sm font-medium">{form.status.toUpperCase()} / {form.discountType} / {form.redemptionMethod}</span>
                </HorizontalStack>
                <HorizontalStack className="justify-between border-b border-border/40 pb-4">
                  <span className="text-sm font-bold text-muted-foreground">Benefit</span>
                  <span className="text-sm font-bold text-success">
                    {form.benefitType === 'percent_off' ? `${form.percentage}% Off` : ''}
                    {['amount_off', 'fixed_price'].includes(form.benefitType)
                      ? `${form.currency || 'USD'} ${form.amount || '0.00'}`
                      : ''}
                    {form.benefitType === 'fee_waiver' ? 'Fee Waiver' : ''}
                  </span>
                </HorizontalStack>
                 <HorizontalStack className="justify-between">
                  <span className="text-sm font-bold text-muted-foreground">Condition</span>
                  <span className="text-sm font-medium">
                    {form.discountType === 'conditional'
                      ? 'AutoPay enabled'
                      : form.conditionType
                        ? `${form.conditionType}: ${form.conditionValue}`
                        : 'None'}
                  </span>
                </HorizontalStack>
              </VerticalStack>
            </div>
          </VerticalStack>
        );

      default:
        return null;
    }
  };

  const valueConfigured = form.benefitType === 'free_periods'
    ? Number.isSafeInteger(Number(form.discountLength)) && Number(form.discountLength) > 0
    : !!(form.percentage || form.amount || form.benefitType === 'fee_waiver');
  const summaryItems = [
    { label: 'Key Set', value: form.promotionKey ? 'Yes' : 'No', complete: !!form.promotionKey },
    { label: 'Name Set', value: form.name ? 'Yes' : 'No', complete: !!form.name },
    { label: 'Value Configured', value: valueConfigured ? 'Yes' : 'No', complete: valueConfigured },
  ];

  return (
    <WizardWorkspace
      isOpen={isOpen}
      title={isEditing ? "Edit Promotion" : "Create Promotion"}
      subtitle="Design a discount campaign with separate qualification and eligibility rules."
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      saveLabel={isEditing ? "Save Changes" : "Create Promotion"}
      summaryEyebrow="Design Health"
      summaryItems={summaryItems}
    >
      {saveError ? (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm font-semibold text-destructive"
        >
          {saveError}
        </div>
      ) : null}
      {renderContent()}
    </WizardWorkspace>
  );
}
